import logger from './logger';

/**
 * IndexedDB wrapper for managing pending background tasks
 * Stores character creation requests, images, and other async operations
 * that need to survive browser/tab closure
 */
class PixleyDB {
  constructor() {
    this.dbName = 'PixleyDB';
    this.version = 1;
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Initialize the database connection
   * @returns {Promise<IDBDatabase>} Database instance
   */
  async init() {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    // Return existing db if already initialized
    if (this.db) {
      return Promise.resolve(this.db);
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        logger.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger.log('✅ IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        logger.log('🔄 Upgrading IndexedDB schema...');

        // Create pendingTasks store
        if (!db.objectStoreNames.contains('pendingTasks')) {
          const store = db.createObjectStore('pendingTasks', { keyPath: 'id' });

          // Create indexes for efficient querying
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });

          logger.log('📋 Created pendingTasks store with indexes');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Store a new pending task
   * @param {Object} task - Task object to store
   * @returns {Promise<void>}
   */
  async storePendingTask(task) {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readwrite');
    const store = transaction.objectStore('pendingTasks');

    return new Promise((resolve, reject) => {
      const request = store.add({
        ...task,
        createdAt: task.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        attempts: 0
      });

      request.onsuccess = () => {
        logger.log('📝 Stored pending task:', task.id);
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to store task:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get a specific pending task by ID
   * @param {string} id - Task ID
   * @returns {Promise<Object|null>} Task object or null if not found
   */
  async getPendingTask(id) {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readonly');
    const store = transaction.objectStore('pendingTasks');

    return new Promise((resolve, reject) => {
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        logger.error('Failed to get task:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Update task status and result
   * @param {string} id - Task ID
   * @param {string} status - New status (pending, processing, completed, failed)
   * @param {Object} result - Optional result data
   * @param {string} error - Optional error message
   * @returns {Promise<void>}
   */
  async updateTaskStatus(id, status, result = null, error = null) {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readwrite');
    const store = transaction.objectStore('pendingTasks');

    const task = await this.getPendingTask(id);
    if (!task) {
      throw new Error(`Task ${id} not found`);
    }

    const updatedTask = {
      ...task,
      status,
      updatedAt: new Date().toISOString(),
      attempts: task.attempts + (status === 'processing' ? 1 : 0)
    };

    if (result) updatedTask.result = result;
    if (error) updatedTask.error = error;

    return new Promise((resolve, reject) => {
      const request = store.put(updatedTask);

      request.onsuccess = () => {
        logger.log(`📊 Updated task ${id} status to ${status}`);
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to update task:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all pending tasks for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of pending tasks
   */
  async getPendingTasksForUser(userId) {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readonly');
    const store = transaction.objectStore('pendingTasks');
    const index = store.index('userId');

    return new Promise((resolve, reject) => {
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const tasks = request.result.filter(task =>
          task.status === 'pending' || task.status === 'processing'
        );
        resolve(tasks);
      };

      request.onerror = () => {
        logger.error('Failed to get pending tasks:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clean up old completed/failed tasks
   * @param {number} maxAge - Max age in milliseconds (default: 24 hours)
   * @returns {Promise<number>} Number of tasks cleaned up
   */
  async cleanupOldTasks(maxAge = 24 * 60 * 60 * 1000) {
    await this.init();

    const cutoff = new Date(Date.now() - maxAge).toISOString();
    const transaction = this.db.transaction(['pendingTasks'], 'readwrite');
    const store = transaction.objectStore('pendingTasks');

    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const task = cursor.value;
          if ((task.status === 'completed' || task.status === 'failed') &&
              task.updatedAt < cutoff) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          logger.log(`🧹 Cleaned up ${deletedCount} old tasks`);
          resolve(deletedCount);
        }
      };

      request.onerror = () => {
        logger.error('Failed to cleanup tasks:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a specific task
   * @param {string} id - Task ID
   * @returns {Promise<void>}
   */
  async deleteTask(id) {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readwrite');
    const store = transaction.objectStore('pendingTasks');

    return new Promise((resolve, reject) => {
      const request = store.delete(id);

      request.onsuccess = () => {
        logger.log(`🗑️ Deleted task ${id}`);
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to delete task:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get database statistics
   * @returns {Promise<Object>} Stats object with task counts
   */
  async getStats() {
    await this.init();

    const transaction = this.db.transaction(['pendingTasks'], 'readonly');
    const store = transaction.objectStore('pendingTasks');

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const tasks = request.result;
        const stats = {
          total: tasks.length,
          pending: tasks.filter(t => t.status === 'pending').length,
          processing: tasks.filter(t => t.status === 'processing').length,
          completed: tasks.filter(t => t.status === 'completed').length,
          failed: tasks.filter(t => t.status === 'failed').length
        };
        resolve(stats);
      };

      request.onerror = () => {
        logger.error('Failed to get stats:', request.error);
        reject(request.error);
      };
    });
  }
}

// Create singleton instance
const pixleyDB = new PixleyDB();

// Auto-cleanup old tasks on initialization
pixleyDB.init().then(() => {
  pixleyDB.cleanupOldTasks().catch(logger.error);
}).catch(logger.error);

export default pixleyDB;

// Named exports for testing
export { PixleyDB };